/**
 * AdminAviationEnglishScenarios — 2-tab CRUD page
 * Tab 1: Scenarios (list + modal)
 * Tab 2: Questions (with scenario_id filter + modal)
 * Route: /admin/aviation-english-scenarios
 * Roles: admin, superadmin
 */
import { useState } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, ConfirmDialog, LoadingState } from '../components/admin';
import DataTable from '../components/admin/DataTable';
import BilingualTextField from '../components/admin/BilingualTextField';
import DynamicListInput from '../components/admin/DynamicListInput';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import {
  useAviationScenarios,
  useCreateAviationScenario,
  useUpdateAviationScenario,
  useDeleteAviationScenario,
  useAviationQuestions,
  useCreateAviationQuestion,
  useUpdateAviationQuestion,
  useDeleteAviationQuestion,
} from '../api/admin/cabinModules';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Plus, Globe, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';

const LEVELS = [
  { value: 'beginner', labelTr: 'Başlangıç', labelEn: 'Beginner' },
  { value: 'intermediate', labelTr: 'Orta', labelEn: 'Intermediate' },
  { value: 'advanced', labelTr: 'İleri', labelEn: 'Advanced' },
];

const EMPTY_SCENARIO = {
  id: '',
  name_tr: '',
  name_en: '',
  description_tr: '',
  description_en: '',
  difficulty: 'beginner',
  order: 0,
  is_active: true,
};

const EMPTY_QUESTION = {
  id: '',
  scenario_id: '',
  text: '',
  context_tr: '',
  answer_type: 'choice',
  options: ['', ''],
  correct_answer: '',
  sample_answer: '',
  points: 10,
  order: 0,
  is_active: true,
};

export default function AdminAviationEnglishScenarios() {
  const { lang, t } = useLanguage();
  const toast = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState('scenarios');

  // Scenario state
  const [scenarioPage, setScenarioPage] = useState(1);
  const [scenarioFilters, setScenarioFilters] = useState({ difficulty: '' });
  const [scenarioModal, setScenarioModal] = useState(false);
  const [editingScenarioId, setEditingScenarioId] = useState(null);
  const [scenarioForm, setScenarioForm] = useState(EMPTY_SCENARIO);
  const [scenarioConfirm, setScenarioConfirm] = useState(false);
  const [deleteScenarioTarget, setDeleteScenarioTarget] = useState(null);

  // Question state
  const [questionPage, setQuestionPage] = useState(1);
  const [questionFilters, setQuestionFilters] = useState({ scenario_id: '' });
  const [questionModal, setQuestionModal] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [questionForm, setQuestionForm] = useState(EMPTY_QUESTION);
  const [questionConfirm, setQuestionConfirm] = useState(false);
  const [deleteQuestionTarget, setDeleteQuestionTarget] = useState(null);
  const [showCorrect, setShowCorrect] = useState(false);

  const activeScenarioFilters = Object.fromEntries(
    Object.entries(scenarioFilters).filter(([, v]) => v !== '')
  );
  const activeQuestionFilters = Object.fromEntries(
    Object.entries(questionFilters).filter(([, v]) => v !== '')
  );

  const { data: scenarioData, isLoading: scenariosLoading } = useAviationScenarios(scenarioPage, activeScenarioFilters);
  const { data: questionData, isLoading: questionsLoading } = useAviationQuestions(questionPage, activeQuestionFilters);

  const createScenario = useCreateAviationScenario();
  const updateScenario = useUpdateAviationScenario();
  const deleteScenario = useDeleteAviationScenario();
  const createQuestion = useCreateAviationQuestion();
  const updateQuestion = useUpdateAviationQuestion();
  const deleteQuestion = useDeleteAviationQuestion();

  const scenarios = scenarioData?.items || scenarioData?.scenarios || [];
  const questions = questionData?.items || questionData?.questions || [];
  const scenarioTotalPages = scenarioData?.pages || Math.ceil((scenarioData?.total || 0) / 20) || 1;
  const questionTotalPages = questionData?.pages || Math.ceil((questionData?.total || 0) / 20) || 1;

  // ── Scenario handlers ────────────────────────────────────────────────────────

  const openCreateScenario = () => {
    setEditingScenarioId(null);
    setScenarioForm(EMPTY_SCENARIO);
    setScenarioModal(true);
  };

  const openEditScenario = (row) => {
    setEditingScenarioId(row.id || row._id);
    setScenarioForm({
      id: row.id || row._id || '',
      name_tr: row.name_tr || row.title_tr || '',
      name_en: row.name_en || row.title_en || '',
      description_tr: row.description_tr || '',
      description_en: row.description_en || '',
      difficulty: row.difficulty || row.level || 'beginner',
      order: row.order ?? 0,
      is_active: row.is_active !== false,
    });
    setScenarioModal(true);
  };

  const scenarioFieldChange = (name, value) => setScenarioForm((f) => ({ ...f, [name]: value }));

  const handleSaveScenario = async () => {
    if (!scenarioForm.name_tr || !scenarioForm.name_en) {
      toast.error(t('admin.aviationEnglish.validationTitleRequired'));
      return;
    }
    try {
      if (editingScenarioId) {
        const { id: _scnId, ...scnUpdate } = scenarioForm;
        await updateScenario.mutateAsync({ id: editingScenarioId, data: scnUpdate });
        toast.success(t('admin.aviationEnglish.scenarioUpdateSuccess'));
      } else {
        await createScenario.mutateAsync(scenarioForm);
        toast.success(t('admin.aviationEnglish.scenarioCreateSuccess'));
      }
      setScenarioModal(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDeleteScenario = async () => {
    try {
      await deleteScenario.mutateAsync(deleteScenarioTarget.id || deleteScenarioTarget._id);
      toast.success(t('admin.aviationEnglish.scenarioDeleteSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
    setScenarioConfirm(false);
    setDeleteScenarioTarget(null);
  };

  // ── Question handlers ────────────────────────────────────────────────────────

  const openCreateQuestion = () => {
    setEditingQuestionId(null);
    setQuestionForm(EMPTY_QUESTION);
    setQuestionModal(true);
  };

  const openEditQuestion = (row) => {
    setEditingQuestionId(row.id || row._id);
    setQuestionForm({
      id: row.id || row._id || '',
      scenario_id: row.scenario_id || '',
      text: row.text || '',
      context_tr: row.context_tr || '',
      answer_type: row.answer_type || (row.question_type === 'choice' ? 'choice' : 'typed'),
      options: row.options?.length ? row.options : ['', ''],
      correct_answer: row.correct_answer || '',
      sample_answer: row.sample_answer || '',
      points: row.points ?? 10,
      order: row.order ?? 0,
      is_active: row.is_active !== false,
    });
    setQuestionModal(true);
  };

  const qFieldChange = (name, value) => setQuestionForm((f) => ({ ...f, [name]: value }));

  const handleSaveQuestion = async () => {
    if (!questionForm.text) {
      toast.error(t('admin.aviationEnglish.validationQuestionRequired'));
      return;
    }
    if (!questionForm.scenario_id) {
      toast.error(t('admin.aviationEnglish.validationScenarioRequired'));
      return;
    }
    const cleanedOptions = (questionForm.options || []).map((o) => String(o).trim()).filter(Boolean);
    const payload = { ...questionForm, options: cleanedOptions };
    try {
      if (editingQuestionId) {
        const { id: _qId, ...qUpdate } = payload;
        await updateQuestion.mutateAsync({ id: editingQuestionId, data: qUpdate });
        toast.success(t('admin.aviationEnglish.questionUpdateSuccess'));
      } else {
        await createQuestion.mutateAsync(payload);
        toast.success(t('admin.aviationEnglish.questionCreateSuccess'));
      }
      setQuestionModal(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDeleteQuestion = async () => {
    try {
      await deleteQuestion.mutateAsync(deleteQuestionTarget.id || deleteQuestionTarget._id);
      toast.success(t('admin.aviationEnglish.questionDeleteSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
    setQuestionConfirm(false);
    setDeleteQuestionTarget(null);
  };

  // ── Scenario columns ─────────────────────────────────────────────────────────

  const scenarioColumns = [
    {
      key: 'name_tr',
      label: t('admin.aviationEnglish.colTitle'),
      sortable: true,
      render: (_, row) => <span className="font-medium text-gray-800 block max-w-[220px] truncate">{row.name_tr || row.title_tr || '—'}</span>,
    },
    {
      key: 'name_en',
      label: t('admin.aviationEnglish.colTitleEn'),
      render: (_, row) => <span className="text-sm text-gray-500 block max-w-[220px] truncate">{row.name_en || row.title_en || '—'}</span>,
    },
    {
      key: 'difficulty',
      label: t('admin.aviationEnglish.colLevel'),
      render: (_, row) => {
        const diff = row.difficulty || row.level;
        const lvl = LEVELS.find((l) => l.value === diff);
        const colors = { beginner: 'bg-emerald-100 text-emerald-700', intermediate: 'bg-amber-100 text-amber-700', advanced: 'bg-rose-100 text-rose-700' };
        const label = lvl ? (lang === 'tr' ? lvl.labelTr : lvl.labelEn) : (diff || '—');
        return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${colors[diff] || 'bg-gray-100 text-gray-600'}`}>{label}</span>;
      },
    },
    {
      key: 'order',
      label: t('admin.aviationEnglish.colOrder'),
      render: (_, row) => <Badge variant="outline">{row.order ?? 0}</Badge>,
    },
{
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => openEditScenario(row)} className="h-7 px-2">
            <Edit size={14} className="mr-1" />
            {t('admin.btn_edit')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDeleteScenarioTarget(row); setScenarioConfirm(true); }} className="h-7 px-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50">
            <Trash2 size={14} className="mr-1" />
            {t('admin.btn_delete')}
          </Button>
        </div>
      ),
    },
  ];

  // ── Question columns ─────────────────────────────────────────────────────────

  const questionColumns = [
    {
      key: 'text',
      label: t('admin.aviationEnglish.colQuestion'),
      render: (_, row) => <span className="text-sm text-gray-800 block max-w-[260px] truncate">{row.text || row.question_text_en || row.question_text_tr || '—'}</span>,
    },
    {
      key: 'answer_type',
      label: t('admin.aviationEnglish.colQuestionType'),
      render: (_, row) => {
        const at = row.answer_type || row.question_type;
        const label = at === 'choice' ? (lang === 'tr' ? 'Çoktan Seçmeli' : 'Multiple Choice') : (lang === 'tr' ? 'Yazılı' : 'Typed');
        return <Badge variant="secondary" className="text-xs">{label}</Badge>;
      },
    },
    {
      key: 'points',
      label: t('admin.aviationEnglish.colPoints'),
      render: (_, row) => <Badge variant="outline">{row.points ?? 10}</Badge>,
    },
    {
      key: 'correct_answer',
      label: t('admin.aviationEnglish.colCorrectAnswer'),
      render: (_, row) => {
        if (!showCorrect) return <button onClick={() => setShowCorrect(true)} className="text-xs text-cyan-500 flex items-center gap-1"><EyeOff size={12} /> {lang === 'tr' ? 'Göster' : 'Reveal'}</button>;
        const value = row.correct_answer || row.sample_answer || '—';
        return <span className="text-xs text-emerald-600 font-medium max-w-[200px] truncate block">{value}</span>;
      },
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => openEditQuestion(row)} className="h-7 px-2">
            <Edit size={14} className="mr-1" />
            {t('admin.btn_edit')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDeleteQuestionTarget(row); setQuestionConfirm(true); }} className="h-7 px-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50">
            <Trash2 size={14} className="mr-1" />
            {t('admin.btn_delete')}
          </Button>
        </div>
      ),
    },
  ];

  const isSavingScenario = createScenario.isPending || updateScenario.isPending;
  const isSavingQuestion = createQuestion.isPending || updateQuestion.isPending;

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('admin.nav.cabinAcademy') },
          { label: t('admin.nav.aviationEnglish') },
        ]}
        title={t('admin.aviationEnglish.title')}
        subtitle={t('admin.aviationEnglish.subtitle')}
        icon={Globe}
        actions={
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={activeTab === 'scenarios' ? openCreateScenario : openCreateQuestion}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              <Plus size={16} />
              {activeTab === 'scenarios' ? t('admin.aviationEnglish.createScenarioBtn') : t('admin.aviationEnglish.createQuestionBtn')}
            </Button>
          </motion.div>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
        <TabsList className="mb-4">
          <TabsTrigger value="scenarios">{t('admin.aviationEnglish.tabScenarios')}</TabsTrigger>
          <TabsTrigger value="questions">{t('admin.aviationEnglish.tabQuestions')}</TabsTrigger>
        </TabsList>

        {/* ── Scenarios Tab ── */}
        <TabsContent value="scenarios">
          <div className="flex gap-3 mb-4">
            <Select value={scenarioFilters.difficulty} onValueChange={(v) => setScenarioFilters((f) => ({ ...f, difficulty: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-40 h-9 text-sm">
                <SelectValue placeholder={t('admin.aviationEnglish.filterLevel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
                {LEVELS.map((l) => (
                  <SelectItem key={l.value} value={l.value}>{lang === 'tr' ? l.labelTr : l.labelEn}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {scenariosLoading ? <LoadingState /> : (
            <DataTable
              columns={scenarioColumns}
              data={scenarios}
              rowKey={(r) => r.id || r._id}
              emptyTitle={t('admin.aviationEnglish.emptyScenarios')}
              emptyDescription={t('admin.aviationEnglish.emptyDesc')}
            />
          )}
          {scenarioTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={scenarioPage <= 1} onClick={() => setScenarioPage((p) => p - 1)}>{t('admin.prev_page')}</Button>
              <span className="text-sm">{scenarioPage} / {scenarioTotalPages}</span>
              <Button variant="outline" size="sm" disabled={scenarioPage >= scenarioTotalPages} onClick={() => setScenarioPage((p) => p + 1)}>{t('admin.next_page')}</Button>
            </div>
          )}
        </TabsContent>

        {/* ── Questions Tab ── */}
        <TabsContent value="questions">
          <div className="flex gap-3 mb-4">
            <Select value={questionFilters.scenario_id} onValueChange={(v) => setQuestionFilters((f) => ({ ...f, scenario_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-56 h-9 text-sm">
                <SelectValue placeholder={t('admin.aviationEnglish.filterScenario')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
                {scenarios.map((s) => (
                  <SelectItem key={s.id || s._id} value={s.id || s._id}>{s.name_tr || s.name_en || s.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5"
              onClick={() => setShowCorrect((v) => !v)}
            >
              {showCorrect ? <EyeOff size={14} /> : <Eye size={14} />}
              {showCorrect ? (lang === 'tr' ? 'Cevapları Gizle' : 'Hide Answers') : (lang === 'tr' ? 'Cevapları Göster' : 'Show Answers')}
            </Button>
          </div>

          {questionsLoading ? <LoadingState /> : (
            <DataTable
              columns={questionColumns}
              data={questions}
              rowKey={(r) => r.id || r._id}
              emptyTitle={t('admin.aviationEnglish.emptyQuestions')}
              emptyDescription={t('admin.aviationEnglish.emptyDesc')}
            />
          )}
          {questionTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={questionPage <= 1} onClick={() => setQuestionPage((p) => p - 1)}>{t('admin.prev_page')}</Button>
              <span className="text-sm">{questionPage} / {questionTotalPages}</span>
              <Button variant="outline" size="sm" disabled={questionPage >= questionTotalPages} onClick={() => setQuestionPage((p) => p + 1)}>{t('admin.next_page')}</Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Scenario Modal ── */}
      <Dialog open={scenarioModal} onOpenChange={setScenarioModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingScenarioId ? t('admin.aviationEnglish.editScenarioTitle') : t('admin.aviationEnglish.createScenarioTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {!editingScenarioId && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">ID (slug) <span className="text-rose-500">*</span></Label>
                <Input value={scenarioForm.id} onChange={(e) => scenarioFieldChange('id', e.target.value)} placeholder="cabin_safety_level1" className="text-sm font-mono" />
              </div>
            )}

            <BilingualTextField
              labelTr={t('admin.aviationEnglish.fieldTitleTr')}
              labelEn={t('admin.aviationEnglish.fieldTitleEn')}
              nameTr="name_tr"
              nameEn="name_en"
              valueTr={scenarioForm.name_tr}
              valueEn={scenarioForm.name_en}
              onChange={scenarioFieldChange}
              required
            />

            <BilingualTextField
              labelTr={t('admin.aviationEnglish.fieldDescTr')}
              labelEn={t('admin.aviationEnglish.fieldDescEn')}
              nameTr="description_tr"
              nameEn="description_en"
              valueTr={scenarioForm.description_tr}
              valueEn={scenarioForm.description_en}
              onChange={scenarioFieldChange}
              multiline
              rows={3}
            />

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">{t('admin.aviationEnglish.fieldLevel')}</Label>
              <Select value={scenarioForm.difficulty} onValueChange={(v) => scenarioFieldChange('difficulty', v)}>
                <SelectTrigger className="h-9 text-sm w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => <SelectItem key={l.value} value={l.value}>{lang === 'tr' ? l.labelTr : l.labelEn}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={scenarioForm.is_active} onCheckedChange={(v) => scenarioFieldChange('is_active', v)} id="aes-is-active" />
              <Label htmlFor="aes-is-active" className="text-sm font-medium cursor-pointer">{t('admin.aviationEnglish.fieldActive')}</Label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setScenarioModal(false)}>{t('admin.btn_cancel')}</Button>
            <Button onClick={handleSaveScenario} disabled={isSavingScenario} className="bg-cyan-500 hover:bg-cyan-600 text-white">
              {isSavingScenario ? t('admin.btn_loading') : t('admin.btn_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Question Modal ── */}
      <Dialog open={questionModal} onOpenChange={setQuestionModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingQuestionId ? t('admin.aviationEnglish.editQuestionTitle') : t('admin.aviationEnglish.createQuestionTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {!editingQuestionId && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">ID (slug) <span className="text-rose-500">*</span></Label>
                <Input value={questionForm.id} onChange={(e) => qFieldChange('id', e.target.value)} placeholder="initial_q1" className="text-sm font-mono" />
              </div>
            )}
            {/* Scenario select */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">
                {t('admin.aviationEnglish.fieldScenario')} <span className="text-rose-500">*</span>
              </Label>
              <Select value={questionForm.scenario_id} onValueChange={(v) => qFieldChange('scenario_id', v)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder={t('admin.select_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {scenarios.map((s) => (
                    <SelectItem key={s.id || s._id} value={s.id || s._id}>{s.name_tr || s.name_en || s.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Question type */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">{t('admin.aviationEnglish.fieldQuestionType')}</Label>
              <Select value={questionForm.answer_type} onValueChange={(v) => qFieldChange('answer_type', v)}>
                <SelectTrigger className="h-9 text-sm w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="choice">{lang === 'tr' ? 'Çoktan Seçmeli' : 'Multiple Choice'}</SelectItem>
                  <SelectItem value="typed">{lang === 'tr' ? 'Yazılı Cevap' : 'Typed Answer'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">
                {t('admin.aviationEnglish.fieldQuestionEn')} <span className="text-rose-500">*</span>
              </Label>
              <Textarea
                value={questionForm.text}
                onChange={(e) => qFieldChange('text', e.target.value)}
                placeholder="What would you say to a passenger requesting..."
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">{t('admin.aviationEnglish.fieldContextTr')}</Label>
              <Textarea
                value={questionForm.context_tr}
                onChange={(e) => qFieldChange('context_tr', e.target.value)}
                placeholder="TR helper context..."
                rows={2}
                className="text-sm"
              />
            </div>

            {/* Options (for choice type) */}
            {questionForm.answer_type === 'choice' && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-600">{t('admin.aviationEnglish.fieldChoices')}</Label>
                <DynamicListInput
                  value={questionForm.options}
                  onChange={(v) => qFieldChange('options', v)}
                  emptyItem={() => ''}
                  addLabel={t('admin.aviationEnglish.addOption')}
                  min={2}
                  max={6}
                  renderItem={(item, idx, onItemChange) => (
                    <Input
                      value={item}
                      onChange={(e) => onItemChange(e.target.value)}
                      placeholder={`Option ${idx + 1}`}
                      className="text-sm"
                    />
                  )}
                />
                <div className="space-y-1.5 mt-2">
                  <Label className="text-xs font-semibold text-emerald-600">
                    {t('admin.aviationEnglish.correctAnswerHint')}
                  </Label>
                  <Input
                    value={questionForm.correct_answer}
                    onChange={(e) => qFieldChange('correct_answer', e.target.value)}
                    placeholder="Exact text of the correct option"
                    className="text-sm"
                  />
                </div>
              </div>
            )}

            {/* Sample answer (for typed type) */}
            {questionForm.answer_type === 'typed' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">{t('admin.aviationEnglish.fieldSampleAnswerEn')}</Label>
                <Textarea
                  value={questionForm.sample_answer}
                  onChange={(e) => qFieldChange('sample_answer', e.target.value)}
                  placeholder="Sample answer in English..."
                  rows={3}
                  className="text-sm"
                />
              </div>
            )}

            {/* Points + Order */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">{t('admin.aviationEnglish.fieldPoints')}</Label>
                <Input type="number" min={1} max={100} value={questionForm.points} onChange={(e) => qFieldChange('points', Number(e.target.value) || 10)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">{t('admin.aviationEnglish.fieldOrder')}</Label>
                <Input type="number" min={0} value={questionForm.order} onChange={(e) => qFieldChange('order', Number(e.target.value) || 0)} className="text-sm" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={questionForm.is_active} onCheckedChange={(v) => qFieldChange('is_active', v)} id="aeq-is-active" />
              <Label htmlFor="aeq-is-active" className="text-sm font-medium cursor-pointer">{t('admin.aviationEnglish.fieldActive')}</Label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setQuestionModal(false)}>{t('admin.btn_cancel')}</Button>
            <Button onClick={handleSaveQuestion} disabled={isSavingQuestion} className="bg-cyan-500 hover:bg-cyan-600 text-white">
              {isSavingQuestion ? t('admin.btn_loading') : t('admin.btn_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={scenarioConfirm}
        onOpenChange={setScenarioConfirm}
        title={t('admin.aviationEnglish.deleteScenarioTitle')}
        description={t('admin.aviationEnglish.deleteScenarioDesc')}
        onConfirm={handleDeleteScenario}
        loading={deleteScenario.isPending}
      />

      <ConfirmDialog
        open={questionConfirm}
        onOpenChange={setQuestionConfirm}
        title={t('admin.aviationEnglish.deleteQuestionTitle')}
        description={t('admin.aviationEnglish.deleteQuestionDesc')}
        onConfirm={handleDeleteQuestion}
        loading={deleteQuestion.isPending}
      />
    </AdminLayout>
  );
}
